import React, { useState, useRef } from 'react';
import Papa, { ParseResult } from 'papaparse';
import { useImport } from '../../context/ImportContext';

interface ImportDialogProps {
  open: boolean;
  onClose: () => void;
  type: 'company' | 'people';
  onImportComplete: () => void;
}

const ImportDialog: React.FC<ImportDialogProps> = ({ open, onClose, type, onImportComplete }) => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const importManager = useImport();

  // Import type configuration for instructions
  const importConfig = {
    company: {
      uniqueField: 'name',
      uniqueFieldLabel: 'company name',
      requiredField: 'name',
      requiredFieldLabel: 'name',
      entityName: 'company',
      entityNamePlural: 'companies',
      templateUrl: 'https://docs.google.com/spreadsheets/d/16zsckyGt9TnjqAmkU6M3Z4nGNxhJCQ4QKc0sIBhDUFc/edit?usp=sharing',
      lookupFields: [
        'country',
        'state_usa',
        'industry_tag',
        'industry_tag_two',
        'industry_tag_three',
        'industry_tag_four'
      ]
    },
    people: {
      uniqueField: 'email',
      uniqueFieldLabel: 'email',
      requiredField: 'email',
      requiredFieldLabel: 'email',
      entityName: 'person',
      entityNamePlural: 'people',
      templateUrl: 'https://docs.google.com/spreadsheets/d/16zsckyGt9TnjqAmkU6M3Z4nGNxhJCQ4QKc0sIBhDUFc/edit?gid=2091927598#gid=2091927598',
      lookupFields: [
        'id_company',
        'country',
        'state_usa',
        'verification_status',
        'industry_tag',
        'industry_tag_two',
        'industry_tag_three',
        'industry_tag_four',
        'p_tag_1',
        'p_tag_2',
        'p_tag_3',
        'p_tag_4'
      ]
    }
  };

  const config = importConfig[type];

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    setFile(selectedFile);
    setError(null);

    // Parse first 10 rows for preview
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      Papa.parse(text, {
        header: true,
        preview: 10,
        complete: (results: ParseResult<any>) => {
          setPreview(results.data);
        },
        error: (error: Error) => {
          setError(`Error parsing CSV: ${error.message}`);
        }
      });
    };
    reader.readAsText(selectedFile);
  };

  const handleImport = async () => {
    if (!file) return;

    setError(null);

    try {
      await importManager.startImport(file, type, onImportComplete);
      // After starting import, immediately minimize to reduce rendering
      setTimeout(() => {
        handleMinimize();
      }, 100);
    } catch (err: any) {
      setError(err.message || 'Failed to start import');
    }
  };

  const handleClose = () => {
    // Only close if not importing or import is complete
    if (importManager.state.progress && 
        (importManager.state.progress.status === 'processing' || 
         importManager.state.progress.status === 'queued')) {
      // Just minimize instead of closing
      handleMinimize();
    } else {
      importManager.close();
      setFile(null);
      setPreview([]);
      setError(null);
      onClose();
    }
  };

  const handleMinimize = () => {
    importManager.minimize();
    setFile(null);
    setPreview([]);
    setError(null);
    onClose(); // Close the dialog UI but keep the import going
  };

  const progress = importManager.state.progress;
  const uploading = importManager.state.isImporting || (progress !== null && (progress.status === 'processing' || progress.status === 'queued'));

  const getProgressPercentage = () => {
    if (!progress || progress.total === 0) return 0;
    return (progress.processed / progress.total) * 100;
  };

  const getStatusColor = () => {
    if (!progress) return '#1976d2';
    if (progress.status === 'completed') return '#2e7d32';
    if (progress.status === 'failed') return '#d32f2f';
    return '#1976d2';
  };

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1300
    }}>
      <div style={{
        backgroundColor: '#fff',
        borderRadius: '4px',
        width: '90%',
        maxWidth: '900px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0px 11px 15px -7px rgba(0,0,0,0.2), 0px 24px 38px 3px rgba(0,0,0,0.14), 0px 9px 46px 8px rgba(0,0,0,0.12)'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 24px',
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'relative'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '1.25rem',
            fontWeight: 'bold',
            textAlign: 'center',
            flex: 1
          }}>
            Import {type === 'company' ? 'Companies' : 'People'}
          </h2>
          {/* Minimize/Close button - shows "-" during progress, "X" otherwise */}
          <button
            onClick={progress ? handleMinimize : handleClose}
            title={progress ? "Minimize" : "Close"}
            style={{
              position: 'absolute',
              right: '8px',
              top: '8px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px',
              color: 'rgba(0, 0, 0, 0.54)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.04)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            {progress ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 13H5v-2h14v2z"/>
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            )}
          </button>
        </div>

        {/* Content */}
        <div style={{
          padding: '24px',
          overflowY: 'auto',
          flex: 1
        }}>
          {/* Instructions */}
          {!progress && (
            <div style={{
              marginBottom: '24px',
              padding: '24px',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px'
            }}>
              <h3 style={{
                margin: '0 0 16px 0',
                fontSize: '0.875rem',
                fontWeight: 600
              }}>
                Import Instructions
              </h3>
              
              <div style={{ marginBottom: '16px' }}>
                <p style={{ margin: '0 0 8px 0', fontSize: '0.875rem', lineHeight: '1.5' }}>
                  <strong>CSV Format Template:</strong> Please refer to the{' '}
                  <a
                    href={config.templateUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontWeight: 600,
                      color: '#1976d2',
                      textDecoration: 'none',
                      fontSize: '0.875rem'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.textDecoration = 'underline';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.textDecoration = 'none';
                    }}
                  >
                    Google Sheets template
                  </a>
                  {' '}for the exact column format. Column names must match exactly to ensure error-free uploads.
                </p>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <p style={{ margin: '0 0 8px 0', fontSize: '0.875rem', lineHeight: '1.5' }}>
                  <strong>How It Works:</strong> This import supports both <strong>inserting new records</strong> and <strong>updating existing ones</strong> in a single upload. The system uses <strong>{config.uniqueFieldLabel}</strong> as the unique identifier to determine whether to insert or update a record.
                </p>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <p style={{ margin: '0 0 8px 0', fontSize: '0.875rem', lineHeight: '1.5' }}>
                  <strong>Required Fields:</strong> The <strong>{config.requiredFieldLabel}</strong> column must have a value for every {config.entityName}. All other columns are optional and can be left empty based on your preferences.
                </p>
              </div>

              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#ffebee', borderRadius: '4px', borderLeft: '4px solid #d32f2f' }}>
                <p style={{ margin: '0', fontSize: '0.875rem', lineHeight: '1.5', color: '#c62828' }}>
                  <strong>Do not close this tab or browser window during the import process.</strong> Closing the tab may disrupt the import and cause it to fail. The import will continue in the background, but you should keep the tab open until the process completes.
                </p>
              </div>

              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fff3e0', borderRadius: '4px', borderLeft: '4px solid #ff9800' }}>
                <p style={{ margin: '0 0 8px 0', fontSize: '0.875rem', lineHeight: '1.5', fontWeight: 600 }}>
                  <strong>Handling Empty Values and NULL:</strong>
                </p>
                <ul style={{ paddingLeft: '24px', margin: '0 0 8px 0', fontSize: '0.875rem', lineHeight: '1.5' }}>
                  <li style={{ marginBottom: '4px' }}>
                    <strong>Empty cells:</strong> If a column is empty or not included in the CSV, the existing value in the database will be preserved (not updated).
                  </li>
                  <li style={{ marginBottom: '4px' }}>
                    <strong>Explicit NULL:</strong> To explicitly set a field to NULL (clear the value), enter <strong>"NULL"</strong> (case-insensitive) in that cell. This will set the field to NULL in the database.
                  </li>
                </ul>
                <p style={{ margin: '0', fontSize: '0.875rem', lineHeight: '1.5', fontStyle: 'italic', color: '#666' }}>
                  <strong>Example:</strong> If a record has <strong>description</strong> = "Some text" and you want to clear it, set <strong>description</strong> = "NULL" in the CSV. If you leave it empty, the existing "Some text" will be preserved.
                </p>
              </div>

              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#e3f2fd', borderRadius: '4px', borderLeft: '4px solid #1976d2' }}>
                <p style={{ margin: '0 0 8px 0', fontSize: '0.875rem', lineHeight: '1.5', fontWeight: 600 }}>
                  <strong>Updating {config.uniqueFieldLabel === 'company name' ? 'Company Name' : 'Email'}:</strong>
                </p>
                <p style={{ margin: '0 0 8px 0', fontSize: '0.875rem', lineHeight: '1.5' }}>
                  To update the {config.uniqueFieldLabel} of an existing record, include both columns in your CSV:
                </p>
                <ul style={{ paddingLeft: '24px', margin: '0 0 8px 0', fontSize: '0.875rem', lineHeight: '1.5' }}>
                  <li style={{ marginBottom: '4px' }}>
                    <strong>{config.uniqueFieldLabel === 'company name' ? 'name' : 'email'}:</strong> Enter the <strong>current/existing</strong> {config.uniqueFieldLabel} (used to find the record)
                  </li>
                  <li style={{ marginBottom: '4px' }}>
                    <strong>{config.uniqueFieldLabel === 'company name' ? 'new_name' : 'new_email'}:</strong> Enter the <strong>new</strong> {config.uniqueFieldLabel} value (this will replace the existing value)
                  </li>
                </ul>
                <p style={{ margin: '0', fontSize: '0.875rem', lineHeight: '1.5', fontStyle: 'italic', color: '#666' }}>
                  <strong>Example:</strong> {config.uniqueFieldLabel === 'company name' ? (
                    <>To rename "ABC Corp" to "ABC Corporation", set <strong>name</strong> = "ABC Corp" and <strong>new_name</strong> = "ABC Corporation". The system will find the record by "ABC Corp" and update the name to "ABC Corporation".</>
                  ) : (
                    <>To update email from "old@example.com" to "new@example.com", set <strong>email</strong> = "old@example.com" and <strong>new_email</strong> = "new@example.com". The system will find the record by "old@example.com" and update the email to "new@example.com".</>
                  )}
                </p>
                <p style={{ margin: '8px 0 0 0', fontSize: '0.875rem', lineHeight: '1.5' }}>
                  <strong>Note:</strong> If <strong>{config.uniqueFieldLabel === 'company name' ? 'new_name' : 'new_email'}</strong> is not provided or is empty, the {config.uniqueFieldLabel} will remain unchanged. You can still update other fields without changing the {config.uniqueFieldLabel}.
                </p>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <p style={{ margin: '0 0 8px 0', fontSize: '0.875rem', lineHeight: '1.5' }}>
                  <strong>Lookup Columns:</strong> The following columns support two input methods:
                </p>
                <ul style={{ paddingLeft: '24px', margin: '0 0 8px 0', fontSize: '0.875rem', lineHeight: '1.5' }}>
                  {config.lookupFields.map((field) => (
                    <li key={field} style={{ marginBottom: '4px' }}>
                      <strong>{field}</strong>
                    </li>
                  ))}
                </ul>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <p style={{ margin: '0 0 8px 0', fontSize: '0.875rem', lineHeight: '1.5' }}>
                  <strong>Input Methods for Lookup Fields:</strong>
                </p>
                <ol style={{ paddingLeft: '24px', margin: '0', fontSize: '0.875rem', lineHeight: '1.5' }}>
                  <li style={{ marginBottom: '4px' }}>
                    <strong>ID Number (Recommended):</strong> Use the numeric ID from the lookup table. This is the most reliable method.
                  </li>
                  <li style={{ marginBottom: '0' }}>
                    <strong>Name:</strong> Enter the exact name as it appears in the lookup table. The backend will automatically convert it to the corresponding ID. <strong>Important:</strong> Any spelling mistakes will cause the record to fail, so ensure names match exactly.
                  </li>
                </ol>
              </div>
            </div>
          )}

          {/* File Upload */}
          {!progress && (
            <div style={{ marginBottom: '24px' }}>
              <input
                type="file"
                ref={fileInputRef}
                accept=".csv"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  border: '1px solid rgba(0, 0, 0, 0.23)',
                  borderRadius: '4px',
                  backgroundColor: '#fff',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  color: uploading ? 'rgba(0, 0, 0, 0.38)' : 'rgba(0, 0, 0, 0.87)',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!uploading) {
                    e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.04)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#fff';
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z"/>
                </svg>
                {file ? file.name : 'Choose CSV File'}
              </button>
              <p style={{
                margin: '8px 0 0 0',
                fontSize: '0.75rem',
                color: 'rgba(0, 0, 0, 0.6)'
              }}>
                CSV file should have column headers matching the database fields
              </p>
            </div>
          )}

          {/* Error Display */}
          {(error || importManager.state.error) && (
            <div style={{
              marginBottom: '16px',
              padding: '16px',
              backgroundColor: '#ffebee',
              border: '1px solid #ef5350',
              borderRadius: '4px',
              color: '#c62828'
            }}>
              {error || importManager.state.error}
            </div>
          )}

          {/* Preview Table */}
          {preview.length > 0 && !progress && (
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{
                margin: '0 0 8px 0',
                fontSize: '0.875rem',
                fontWeight: 500
              }}>
                Preview (First 10 rows)
              </h4>
              <div style={{
                maxHeight: '300px',
                overflow: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: '4px'
              }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '0.875rem'
                }}>
                  <thead style={{
                    position: 'sticky',
                    top: 0,
                    backgroundColor: '#f5f5f5',
                    zIndex: 1
                  }}>
                    <tr>
                      {Object.keys(preview[0]).map((key) => (
                        <th key={key} style={{
                          padding: '12px',
                          textAlign: 'left',
                          fontWeight: 'bold',
                          borderBottom: '1px solid #e0e0e0'
                        }}>
                          {key}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((row, idx) => (
                      <tr key={idx}>
                        {Object.values(row).map((value: any, cellIdx) => (
                          <td key={cellIdx} style={{
                            padding: '12px',
                            borderBottom: '1px solid #e0e0e0'
                          }}>
                            {value}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Progress Display */}
          {progress && (
            <div>
              {/* Progress Bar */}
              <div style={{
                marginBottom: '16px',
                width: '100%',
                height: '10px',
                backgroundColor: '#e0e0e0',
                borderRadius: '5px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${getProgressPercentage()}%`,
                  height: '100%',
                  backgroundColor: getStatusColor(),
                  transition: 'width 0.3s ease'
                }} />
              </div>

              {/* Status and Count */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px'
              }}>
                <span style={{
                  padding: '4px 12px',
                  borderRadius: '16px',
                  backgroundColor: getStatusColor() === '#2e7d32' ? '#c8e6c9' :
                                   getStatusColor() === '#d32f2f' ? '#ffcdd2' :
                                   '#bbdefb',
                  color: getStatusColor() === '#2e7d32' ? '#1b5e20' :
                         getStatusColor() === '#d32f2f' ? '#b71c1c' :
                         '#0d47a1',
                  fontSize: '0.75rem',
                  fontWeight: 500
                }}>
                  Status: {progress.status.toUpperCase()}
                </span>
                <span style={{ fontSize: '0.875rem' }}>
                  {progress.processed} / {progress.total} rows processed
                </span>
              </div>

              {/* Stats Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '16px',
                marginBottom: '16px'
              }}>
                <div style={{
                  textAlign: 'center',
                  padding: '16px',
                  backgroundColor: '#c8e6c9',
                  borderRadius: '4px'
                }}>
                  <div style={{ fontSize: '2rem', fontWeight: 500, marginBottom: '4px' }}>
                    {progress.inserted}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                    Inserted
                  </div>
                </div>
                <div style={{
                  textAlign: 'center',
                  padding: '16px',
                  backgroundColor: '#bbdefb',
                  borderRadius: '4px'
                }}>
                  <div style={{ fontSize: '2rem', fontWeight: 500, marginBottom: '4px' }}>
                    {progress.updated}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                    Updated
                  </div>
                </div>
                <div style={{
                  textAlign: 'center',
                  padding: '16px',
                  backgroundColor: '#ffcdd2',
                  borderRadius: '4px'
                }}>
                  <div style={{ fontSize: '2rem', fontWeight: 500, marginBottom: '4px' }}>
                    {progress.failed}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                    Failed
                  </div>
                </div>
              </div>

              {/* Errors Table */}
              {progress.errors.length > 0 && (
                <div>
                  <h4 style={{
                    margin: '0 0 8px 0',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#d32f2f'
                  }}>
                    Errors ({progress.errors.length})
                  </h4>
                  <div style={{
                    maxHeight: '400px',
                    overflow: 'auto',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px'
                  }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '0.875rem'
                    }}>
                      <thead style={{
                        position: 'sticky',
                        top: 0,
                        backgroundColor: '#f5f5f5',
                        zIndex: 1
                      }}>
                        <tr>
                          <th style={{
                            padding: '12px',
                            textAlign: 'left',
                            fontWeight: 'bold',
                            borderBottom: '1px solid #e0e0e0'
                          }}>
                            Row
                          </th>
                          <th style={{
                            padding: '12px',
                            textAlign: 'left',
                            fontWeight: 'bold',
                            borderBottom: '1px solid #e0e0e0'
                          }}>
                            Data
                          </th>
                          <th style={{
                            padding: '12px',
                            textAlign: 'left',
                            fontWeight: 'bold',
                            borderBottom: '1px solid #e0e0e0'
                          }}>
                            Error
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {progress.errors.map((err, idx) => (
                          <tr key={idx}>
                            <td style={{
                              padding: '12px',
                              borderBottom: '1px solid #e0e0e0'
                            }}>
                              {err.row}
                            </td>
                            <td style={{
                              padding: '12px',
                              borderBottom: '1px solid #e0e0e0'
                            }}>
                              {err.data || '-'}
                            </td>
                            <td style={{
                              padding: '12px',
                              borderBottom: '1px solid #e0e0e0'
                            }}>
                              {err.error}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer - only show Start Import button when no progress */}
        {!progress && (
          <div style={{
            padding: '12px 24px',
            borderTop: '1px solid rgba(0, 0, 0, 0.12)',
            display: 'flex',
            justifyContent: 'flex-end',
          }}>
            <button
              onClick={handleImport}
              disabled={!file || uploading}
              style={{
                padding: '8px 24px',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: '#1976d2',
                color: '#fff',
                cursor: (!file || uploading) ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                fontWeight: 500,
                opacity: (!file || uploading) ? 0.5 : 1
              }}
              onMouseEnter={(e) => {
                if (!(!file || uploading)) {
                  e.currentTarget.style.backgroundColor = '#1565c0';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#1976d2';
              }}
            >
              Start Import
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportDialog;
